import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { RouterModule } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import Organization from "models/Organization";
import { SitemapNode } from "screens/app/constants/site-map";
import { FormattersService } from "services/formatters.service";
import { NavigationService } from "services/navigation.service";
import { OrgFacade } from "state/org.facade";

@Component({
  selector: "app-breadcrumbs",
  templateUrl: "./breadcrumbs.component.html",
  styleUrls: ["./breadcrumbs.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [RouterModule, TranslateModule],
})
export class BreadcrumbsComponent {
  protected readonly navigation = inject(NavigationService);
  private readonly orgFacade = inject(OrgFacade);
  private readonly formatterService = inject(FormattersService);
  private readonly translate = inject(TranslateService);

  private readonly org = toSignal<Organization | null>(this.orgFacade.org$);

  protected queryParamsOf(item: SitemapNode): Record<string, string> {
    return this.navigation.queryParamsOf(item);
  }

  protected crumbLabel(item: SitemapNode): string {
    return item.text ?? this.breadcrumbTitle(item.route);
  }

  private breadcrumbTitle(route: string): string {
    route = route.replace("/", "");

    if (route === "employee") {
      return this.org()
        ? this.translate.instant(this.formatterService.orgFormat(this.org()))
        : this.translate.instant("shared.navigation_bar.employee");
    }

    if (route === "employees-list") {
      return this.org()
        ? this.translate.instant(this.formatterService.orgFormat(this.org(), true))
        : this.translate.instant("shared.navigation_bar.employees-list");
    }

    // Breadcrumb mirrors the upload page title rather than the nav link label ("Upload")
    if (route === "upload") {
      return this.translate.instant("screens.upload.add_assessment_title");
    }

    if (route === "manual") {
      return this.translate.instant("screens.upload.add_manual_assessment_title");
    }

    const knownRoutes = ["analytics", "admin", "dev"];
    if (knownRoutes.includes(route)) {
      return this.translate.instant(`shared.navigation_bar.${route}`);
    }

    const translated = this.translate.instant(`shared.navigation_bar.${route}`);
    const hasTranslation = translated !== `shared.navigation_bar.${route}`;

    if (hasTranslation) {
      return translated;
    }

    return (
      route.charAt(0).toUpperCase() +
      route.slice(1) +
      this.translate.instant("shared.navigation_bar.page_suffix")
    );
  }
}
